import React, { useState, useEffect } from 'react';
import { Building2, Edit3, Check, X, FileText } from 'lucide-react';
import Card from '../../../components/ui/card';
import Button from '../../../components/ui/button';
import Input from '../../../components/ui/input';

const BrokerInfoCard = ({ client, clientDataHook }) => {
	const { updateCard, isCardEditMode, toggleCardEditMode, cancelCardEdit, isOnboarding, refreshData, isLoading } = clientDataHook;
	const cardName = 'brokerInfo';
	const isEditing = isOnboarding || isCardEditMode(cardName);
	
	// Form state for onboarding mode
	const [formValues, setFormValues] = useState({
		brokerName: client?.brokerName || '',
		brokerLicense: client?.brokerLicense || ''
	});

	// Refresh data when component mounts (for onboarding)
	useEffect(() => {
		if (isOnboarding && refreshData) {
			console.log('🟦 BrokerInfoCard - Component mounted, refreshing data');
			refreshData();
		}
	}, [isOnboarding]); // Remove refreshData from dependencies to prevent infinite loop

	// Update form values when client data changes
	useEffect(() => {
		setFormValues({
			brokerName: client?.brokerName || '',
			brokerLicense: client?.brokerLicense || ''
		});
	}, [client]);

	const fields = [
		{
			key: 'brokerName',
			label: 'Company (Your Brokerage)',
			type: 'text',
			value: client?.brokerName || '',
			icon: Building2,
			placeholder: 'Your brokerage company name',
		},
		{
			key: 'brokerLicense',
			label: 'Brokerage License #',
			type: 'text',
			value: client?.brokerLicense || '',
			icon: FileText,
			placeholder: 'Brokerage license number',
		},
	];

	// Handle save for profile mode (card-wide editing)
	const handleSave = async () => {
		const success = await updateCard(formValues);
		if (success) {
			toggleCardEditMode(cardName); // Exit edit mode
		}
	};

	// Handle cancel for profile mode (card-wide editing)
	const handleCancel = () => {
		// Reset form values to original client data
		setFormValues({
			brokerName: client?.brokerName || '',
			brokerLicense: client?.brokerLicense || ''
		});
		cancelCardEdit(cardName);
	};
	
	// Handle form field changes for onboarding
	const handleFormFieldChange = (fieldKey, value) => {
		setFormValues(prev => ({
			...prev,
			[fieldKey]: value
		}));
	};
	
	// Handle save for onboarding mode
	const handleOnboardingSave = async () => {
		const success = await updateCard(formValues);
		return success;
	};

	return (
		<Card>
			{!isOnboarding && (
				<Card.Header className='flex flex-row items-center justify-center space-y-0 pb-4'>
					<div className='space-y-1 text-center'>
						<Card.Title className='flex items-center justify-center gap-2 text-lg font-montserrat-medium text-gray-900'>
							<Building2 className='h-5 w-5' />
							Brokerage Information
						</Card.Title>
						<Card.Description className='text-sm text-gray-600'>
							Information about your brokerage and licensing.
						</Card.Description>
					</div>
					{!isEditing && (
						<Button
							size='sm'
							variant='outline'
							onClick={() => toggleCardEditMode(cardName)}
							className='border-gray-300 text-gray-600 hover:bg-gray-50 ml-4'>
							<Edit3 className='h-4 w-4 mr-2' />
							Edit
						</Button>
					)}
				</Card.Header>
			)}
			<Card.Content className='space-y-4'>
				{fields.map((field) => (
					<div key={field.key} className='space-y-2'>
						<label className='text-sm font-semibold text-gray-800'>
							{field.label}
						</label>
						
						{isEditing ? (
							<Input
								type={field.type}
								value={formValues[field.key] || ''}
								onChange={(e) => handleFormFieldChange(field.key, e.target.value)}
								placeholder={field.placeholder}
								className='w-full'
							/>
						) : (
							<div className='flex items-start gap-2'>
								<field.icon className='h-4 w-4 text-gray-500 mt-0.5' />
								<div className='flex-1'>
									<span className='text-sm font-montserrat-regular text-gray-900'>
										{field.value || (
											<span className='text-gray-400 italic'>
												{field.placeholder}
											</span>
										)}
									</span>
								</div>
							</div>
						)}
					</div>
				))}
				
				{/* Save/Cancel buttons when editing in profile mode */}
				{isEditing && !isOnboarding && (
					<div className='flex justify-end gap-3 pt-4 border-t border-gray-200'>
						<Button
							variant='outline'
							onClick={handleCancel}
							className='border-gray-300 text-gray-600 hover:bg-gray-50'>
							<X className='h-4 w-4 mr-2' />
							Cancel
						</Button>
						<Button
							onClick={handleSave}
							className='bg-gray-900 hover:bg-gray-800 text-white'>
							<Check className='h-4 w-4 mr-2' />
							Save Changes
						</Button>
					</div>
				)}
				
				{/* Save button for onboarding */}
				{isOnboarding && (
					<div className='pt-6 mt-6 border-t border-gray-200'>
						<Button
							onClick={handleOnboardingSave}
							className='w-full bg-gray-900 hover:bg-gray-800 text-white'>
							<Check className='h-4 w-4 mr-2' />
							Save & Continue
						</Button>
					</div>
				)}
			</Card.Content>
		</Card>
	);
};

export default BrokerInfoCard;